import React, { Fragment, useMemo, useEffect } from 'react';
import colorAlpha from 'color-alpha';
import colorVariables from '@densityco/ui/variables/colors.json';
import fontVariables from '@densityco/ui/variables/fonts.json';
import {
  Dialogger,
  Icons 
} from '@densityco/ui';

import { approximatelyEqual } from '../DualCaptureViewer';
import { ON_PREM } from '../../../utils/environment';

import { renderTimelineEvents } from '../../../utils/capture-viewer';

import {
  coordinateToDisplay,
  displayToCoordinate,
  TICK_BAR_HEIGHT,
  TRACK_HEIGHT,
} from './constants';
import { Track, RenderedTimelineEvent } from '.';

import styles from './styles.scss';
import MPIEventTimelineStore from '../../../rx-stores/event-timeline';
import { 
  EventTimelineActionTypes,
  RenderedTimelineEventWithTrackOffset } from '../../../types/event-timeline';
import useRxStore from '../../../utils/use-rx-store';
import useRxDispatch from '../../../utils/use-rx-dispatch';

type TrackTimelineEventsProps = {
  track: Track;
  trackIndex: number;
  trackLengthInSeconds?: number;

  seek: number;
  onSeek: (seek: number) => void;
};

export type RenderedTimelineEventWithType = RenderedTimelineEvent & { type: string };

// This component is rendered for each track shown in the "fine" timeline within in the viewer
// playback controls. It renders all ingresses and egresses for that track.
const TrackTimelineEvents: React.FunctionComponent<TrackTimelineEventsProps> = ({
  track,
  trackIndex,
  trackLengthInSeconds,

  seek,
  onSeek,
}) => {

  const state = useRxStore(MPIEventTimelineStore);
  const dispatch = useRxDispatch();
  // Clear changes when leaving or reloading the page
  useEffect(() => {
    dispatch({
      type: EventTimelineActionTypes.EVENT_TIMELINE_CLEAR_CHANGES
    })
  }, [dispatch])
  const yPosition = TICK_BAR_HEIGHT + trackIndex * TRACK_HEIGHT;
  const trackSectionHeight = TRACK_HEIGHT / 2;

  const annotationEventsYPosition = yPosition;
  const algoEventsYPosition =
    (ON_PREM ? annotationEventsYPosition : yPosition) + trackSectionHeight;

  const timelineEvents: Array<RenderedTimelineEventWithType> = [
    ...renderTimelineEvents(track.annotationEvents).map(event => ({
      type: 'annotation' as const,
      ...event,
    })),
    ...renderTimelineEvents(track.algoEvents).map(event => ({
      type: 'algo' as const,
      ...event,
    }))
  ];

  const ingressOffset = 0.5 * trackSectionHeight;
  const egressOffset = 0.75 * trackSectionHeight;

  const getTimelineEventCoordinate = (
    event: RenderedTimelineEventWithType,
  ): [number, number] => {
    const xInSeconds = event.secondsOffset - track.offsetInSeconds;
    const x = coordinateToDisplay(xInSeconds);

    if (event.type === 'annotation') {
      return [
        x,
        annotationEventsYPosition +
          (event.direction === 1 ? ingressOffset : egressOffset),
      ];
    } else {
      return [
        x,
        algoEventsYPosition +
          (event.direction === 1 ? ingressOffset : egressOffset),
      ];
    }
  };

const repositionEvent = async (event, track, onSeek) => {
  // Algo event repositioning is not allowed
  if (event.type === 'algo') {
    return;
  }
  const response = await Dialogger.prompt({
    title: 'Reposition Annotation',
    prompt: 'Enter a new time (in seconds)',
  })
  const newSecondOffset = parseFloat(response);
  if (!newSecondOffset) {
    return;
  }
  dispatch({
    type: EventTimelineActionTypes.EVENT_TIMELINE_ADD_CHANGE,
    change: {
      ...event,
      secondsOffset: (newSecondOffset + track.offsetInSeconds).toPrecision(4),
      secondsInTrack: newSecondOffset
    }
  })
  onSeek(newSecondOffset);
}

  return (
    <Fragment>
      {timelineEvents.map(event => {
        if (ON_PREM && event.type === 'annotation') {
          return null;
        }

{        const eventWithPendingChange = state.pendingChanges.filter(i => i.id === event.id);

        if (eventWithPendingChange.length) {
          let pendingChange: any = JSON.parse(JSON.stringify(eventWithPendingChange[0]));
          // Make it type compatible
          delete pendingChange['trackOffset'];
          event = {
            ...pendingChange,
            type: event.type,

          }
        }
}
        const [x, y] = getTimelineEventCoordinate(event);

        const xInSeconds = event.secondsOffset - track.offsetInSeconds;
        if (xInSeconds < 0 || xInSeconds > trackLengthInSeconds) {
          // Don't show events that are before the start time or after the end time of the capture.
          //
          // This condition gets hit with MPIs or whenever there is a `secondsOffset`
          // specified for one of the sensor tracks.
          return null;
        }

        const isSelected = approximatelyEqual(seek, xInSeconds);

        let fill: string = '';
        if (event.filled && isSelected) {
          fill = colorVariables.red;
        } else if (event.filled) {
          fill = colorAlpha(
            event.type === 'annotation'
              ? colorVariables.green
              : colorVariables.purple,
            0.5,
          );
        } else {
          fill = 'transparent';
        }

        let stroke: string = '';
        if (isSelected) {
          stroke = colorVariables.red;
        } else if (event.type === 'annotation') {
          stroke = colorVariables.green;
        } else {
          stroke = colorVariables.purple;
        }
        if (event.visibleInTrack) {
        return (
          <Fragment
            key={event.id}
          >
          <g key={`marker${event.id}`} transform={`translate(${x},${y})`}>
            {/* This is the visible triangular marker */}
            <path
              fill={fill}
              stroke={stroke}
              strokeWidth="0.75"
              transform={`scale(2.25) rotate(${
                event.direction === 1 ? 180 : 0
              })`}
              d="M-5 -2L-0.0349076676 2.10470017 4.96096292 -2z"
            />

            {/* When selected, show a tooltip with the x and y coordinate */}
            {isSelected && event.x && event.y && event.type === 'annotation' ? (
            <Fragment
            >
              <g
              >
                <rect
                  fill={colorVariables.midnight}
                  x={8}
                  y={8}
                  width={84}
                  height={24}
                  rx={4}
                  ry={4}
                />
                <text
                  transform="translate(14, 21)"
                  alignmentBaseline="middle"
                  fontSize={14}
                  fontFamily={fontVariables.fontBase}
                  fontWeight={500}
                  fill="white"
                >
                  X:{event.x} Y:{event.y}
                </text>
              </g>
              <g
                onClick={() => repositionEvent(event, track, onSeek)}
                style={{cursor: 'pointer'}}
              >
                <rect
                  stroke={colorVariables.gray400}
                  strokeWidth={1}
                  fill={colorVariables.white}
                  x={8}
                  y={-28}
                  width={36}
                  height={20}
                  rx={4}
                  ry={4}
                />
                <text
                  transform="translate(14, -16)"
                  alignmentBaseline="middle"
                  fontSize={10}
                  fontFamily={fontVariables.fontBase}
                  fontWeight={500}
                  fill={colorVariables.midnight}
                >
                  Move
                </text>
              </g>}
            </Fragment>
            ) : null}

            {/* This shape is scaled larger and used for click detection
                on top of the regular triangle
            */}
            <rect
              fill="transparent"
              x={-8}
              y={-5}
              width={15}
              height={10}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (
                  typeof event.x === 'number' &&
                  typeof event.y === 'number'
                ) {
                  onSeek(xInSeconds);
                }
              }}
            />
          }
          </g>
          </Fragment>
        );
      }
      else {return null}
      })}

      {/* Above all timeline events, render any overlapping numbers. */}
      {timelineEvents.map(event => {
        if (!('label' in event) || !event.label || event.label === 1) {
          return null;
        }
        const [x, y] = getTimelineEventCoordinate(event);

        let color: string = '';
        let backgroundColor: string = '';
        if (event.filled) {
          color = colorVariables.white;
          if (event.type === 'annotation') {
            backgroundColor = colorVariables.green;
          } else {
            backgroundColor = colorVariables.purple;
          }
        } else if (event.type === 'annotation') {
          color = colorVariables.green;
        } else {
          color = colorVariables.purple;
        }

        return (
          <Fragment key={`wrapper${event.id}`}>
          <circle
            stroke={event.filled ? backgroundColor : color}
            fill={backgroundColor}
            fillOpacity={backgroundColor ? 1 : 0}
            cx={x} 
            cy={event.direction === 1 ? y-16 : y+16} 
            r={6}
          />
          <text
            key={event.id}
            transform={event.direction === 1 ? `translate(${x},${y - 15})` : `translate(${x},${y + 17})`}
            fill={color}
            fontSize={10}
            fontWeight="bold"
            alignmentBaseline="middle"
            textAnchor="middle"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {event.label}
          </text>
          </Fragment>
        );
      })}
    </Fragment>
  );
};

export default TrackTimelineEvents;
